import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolderOpen, faMagnifyingGlass } from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import { CC_SEARCH } from "@/components/case-checking/caseCheckingChrome";
import {
  CaseDocumentIngestButtons,
  type CaseDocumentIngestProps,
} from "@/components/case-checking/CaseDocumentIngestButtons";
import { DocumentSortToggle } from "@/components/case-checking/DocumentSortToggle";
import { DocumentClassificationFilterChips } from "@/components/case-checking/DocumentClassificationFilterChips";
import type { DocumentClassificationFilterId, DocumentClassificationFilterOption } from "@/components/case-checking/documentClassificationFilters";
import type { DocumentSortMode } from "@/components/case-checking/documentListSort";
import { Button } from "@/components/ui/button/Button";
import FormInput from "@/components/ui/form-input";

export function CaseDocumentsToolbar({
  search,
  onSearchChange,
  sortMode,
  onSortModeChange,
  searchInputId,
  searchPlaceholder = "Search documents...",
  documentIngest,
  showBrowseCrmFallback = false,
  onBrowseCrmFallback,
  testIdPrefix = "case-documents",
  classificationFilter,
  onClassificationFilterChange,
  classificationFilterOptions,
  comfortable = false,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  sortMode: DocumentSortMode;
  onSortModeChange: (mode: DocumentSortMode) => void;
  searchInputId: string;
  searchPlaceholder?: string;
  documentIngest?: CaseDocumentIngestProps;
  showBrowseCrmFallback?: boolean;
  onBrowseCrmFallback?: () => void;
  testIdPrefix?: string;
  classificationFilter?: DocumentClassificationFilterId;
  onClassificationFilterChange?: (id: DocumentClassificationFilterId) => void;
  classificationFilterOptions?: DocumentClassificationFilterOption[];
  /** Extra vertical rhythm (review workflow documents step). */
  comfortable?: boolean;
}) {
  const hasActions = Boolean(documentIngest) || showBrowseCrmFallback;
  const showClassificationChips =
    classificationFilterOptions &&
    classificationFilter !== undefined &&
    onClassificationFilterChange;

  return (
    <div
      className={clsx(comfortable ? "space-y-3" : "space-y-2")}
      data-testid="case-documents-toolbar"
    >
    <div className={clsx("flex flex-wrap items-center", comfortable ? "gap-3" : "gap-2")}>
      <div className="min-w-0 flex-1 basis-full sm:basis-[min(100%,20rem)]">
        <FormInput
          id={searchInputId}
          type="search"
          aria-label="Search documents"
          placeholder={searchPlaceholder}
          value={search}
          icon={<FontAwesomeIcon icon={faMagnifyingGlass} className="h-3.5 w-3.5 text-slate-400" />}
          iconPosition="start"
          className={clsx(CC_SEARCH, "w-full !h-9")}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
      <DocumentSortToggle value={sortMode} onChange={onSortModeChange} />
      {hasActions ? (
        <div className="flex h-9 shrink-0 items-center gap-1.5 border-l border-slate-200 pl-2 dark:border-white/10">
          {documentIngest ? (
            <CaseDocumentIngestButtons
              documentIngest={documentIngest}
              variant="toolbar"
              testIdPrefix={testIdPrefix}
            />
          ) : null}
          {showBrowseCrmFallback && onBrowseCrmFallback ? (
            <Button
              plain
              size="unstyled"
              type="button"
              className="inline-flex shrink-0 items-center gap-1.5 px-0 text-xs font-medium text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              onClick={onBrowseCrmFallback}
              data-testid="browse-crm-documents"
            >
              <FontAwesomeIcon icon={faFolderOpen} className="h-3.5 w-3.5" />
              Browse CRM
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
    {showClassificationChips ? (
      <DocumentClassificationFilterChips
        options={classificationFilterOptions}
        value={classificationFilter}
        onChange={onClassificationFilterChange}
        testIdPrefix={testIdPrefix}
        className={clsx("w-full", comfortable ? "gap-2 pt-0.5" : undefined)}
      />
    ) : null}
    </div>
  );
}
